import HeroBackdrop from "@/components/aoria/HeroBackdrop";

/**
 * PageHeader — composant unifié de haut de page pour toutes les pages internes.
 *
 * Signature visuelle Aoria :
 *   ─ EYEBROW
 *   H1 (italique mesuré)
 *   Lead
 *
 * Le « tick » (trait horizontal fin) précède systématiquement l'eyebrow.
 * Un motif de fond très subtil (HeroBackdrop) peut être ajouté via la prop
 * `backdrop` (variant : flows | weave | modules | network | dots | whisper |
 * lines). Tone "dark" pour les sections sombres.
 */
export default function PageHeader({
    eyebrow,
    title,
    italic,
    lead,
    testid,
    children,
    tone = "light",
    backdrop = null,
}) {
    const isDark = tone === "dark";
    return (
        <section
            data-testid={testid ? `${testid}-header-section` : "page-header-section"}
            className="relative overflow-hidden"
        >
            {backdrop && (
                <HeroBackdrop variant={backdrop} tone={tone} />
            )}
            <header
                data-testid={testid ? `${testid}-header` : "page-header"}
                className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-16 md:pb-24"
            >
                {children}

                {/* Signature : tick + eyebrow */}
                <div className="reveal flex items-center gap-4">
                    <span
                        aria-hidden="true"
                        className={`inline-block h-px w-9 ${
                            isDark ? "bg-[#A8B5C8]" : "bg-[#0D1B2A]/40"
                        }`}
                    />
                    <span
                        data-testid={testid ? `${testid}-eyebrow` : undefined}
                        className={`eyebrow ${isDark ? "!text-[#A8B5C8]" : ""}`}
                    >
                        {eyebrow}
                    </span>
                </div>

                <h1
                    data-testid={testid ? `${testid}-title` : "page-title"}
                    className={`reveal delay-1 h1 mt-8 text-4xl md:text-5xl lg:text-[4.75rem] leading-[1.04] max-w-4xl ${
                        isDark ? "!text-[#FDFDFC]" : "text-[#0D1B2A]"
                    }`}
                >
                    {title}
                    {italic && (
                        <>
                            {" "}
                            <span
                                className={`italic ${
                                    isDark
                                        ? "!text-[#A8B5C8]"
                                        : "text-[#415A77]"
                                }`}
                            >
                                {italic}
                            </span>
                        </>
                    )}
                    {/* Pas de point final si le titre/italique se termine déjà par
                        une ponctuation forte (?, !, ., …) */}
                    {!/[.?!…]$/.test((italic || title).trim()) && "."}
                </h1>

                {lead && (
                    <p
                        data-testid={testid ? `${testid}-lead` : undefined}
                        className={`reveal delay-2 mt-10 md:mt-12 text-lg md:text-xl leading-relaxed max-w-3xl ${
                            isDark ? "!text-[#FDFDFC]/85" : "text-[#5C6B7D]"
                        }`}
                    >
                        {lead}
                    </p>
                )}
            </header>
        </section>
    );
}
